import type {MarkdownEditHistoryViewer_comment$key} from '@github-ui/markdown-edit-history-viewer'
import {MarkdownEditHistoryViewer} from '@github-ui/markdown-edit-history-viewer'
import {PencilIcon} from '@primer/octicons-react'
import {Box, IconButton, RelativeTime, Text} from '@primer/react'

import {TEST_IDS} from '../constants'
import type {IssueBodyHeaderAuthor$key} from './__generated__/IssueBodyHeaderAuthor.graphql'
import {IssueBodyAuthorAvatar} from './IssueBodyAuthorAvatar'
import {IssueBodyHeaderAuthor} from './IssueBodyHeaderAuthor'

type IssueBodyHeaderProps = {
  author: IssueBodyHeaderAuthor$key | null
  editHistory: MarkdownEditHistoryViewer_comment$key
  createdAt: string
  viewerCanUpdate: boolean
  startIssueBodyEdit: () => void
  editInProgress: boolean
}

export function IssueBodyHeader({
  author,
  createdAt,
  editHistory,
  viewerCanUpdate,
  startIssueBodyEdit,
  editInProgress,
}: IssueBodyHeaderProps) {
  return (
    <Box
      sx={{
        backgroundColor: 'canvas.subtle',
        borderTopLeftRadius: 2,
        borderTopRightRadius: 2,
        borderBottom: '1px solid',
        borderBottomColor: 'border.muted',
        color: 'fg.muted',
        display: 'flex',
        flexDirection: 'row',
        flex: 1,
        fontSize: 1,
        gap: 0,
        py: 2,
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          px: 3,
          flexGrow: 1,
          flexShrink: 1,
          flexDirection: 'row',
          gap: 2,
          alignItems: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        <IssueBodyAuthorAvatar author={author} />
        <Box
          as="h3"
          sx={{
            display: 'flex',
            flexDirection: ['column', 'column', 'row', 'row'],
            flexWrap: 'wrap',
            columnGap: 1,
            overflow: 'hidden',
            fontSize: 'unset',
            fontWeight: 'unset',
            margin: 'unset',
          }}
        >
          {author && <IssueBodyHeaderAuthor showLogin author={author} />}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              fontSize: [0, 1, 1, 1],
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <Text sx={{fontSize: 'inherit', whiteSpace: 'nowrap', display: ['none', 'inline', 'inline', 'inline']}}>
              opened this issue
            </Text>
            <RelativeTime sx={{fontSize: 'inherit'}} date={new Date(createdAt)} />
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          gap: 1,
          pl: [0, 3, 3],
          pr: 2,
          display: 'flex',
          flexGrow: 1,
          flexDirection: 'row',
          justifyContent: 'flex-end',
        }}
      >
        <MarkdownEditHistoryViewer editHistory={editHistory} />
        {viewerCanUpdate && !editInProgress && (
          <IconButton
            variant="invisible"
            aria-label="Edit issue"
            icon={PencilIcon}
            size="medium"
            onClick={startIssueBodyEdit}
            data-testid={TEST_IDS.editIssueBodyButton}
          />
        )}
      </Box>
    </Box>
  )
}

try{ IssueBodyHeader.displayName ||= 'IssueBodyHeader' } catch {}